/**
 * CurrentBundleCard — large card for the actively-studied bundle.
 *
 * Design source: /tmp/arianna-design/ios-reader/project/library.jsx — CurrentBundleCard function.
 * Literal replica of the iPad design: accent border + accent-bg ring, large BundleCover,
 * serif title 24px, italic EB Garamond subtitle, progress bar, "▶ Continua" CTA.
 *
 * Styling: Tailwind v4 DS-native utilities + clsx for conditionals.
 */

import * as React from 'react'
import { clsx } from 'clsx'
import { BundleCover } from './BundleCover.js'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CurrentBundleCardBundle {
  code: string
  title: string
  subtitle: string | null
  cat: string
  version: string
  /** null means exam has passed */
  daysToExam: number | null
  /** Displayed as "esame {examDate}" */
  examDate: string | null
  prof: string | null
}

export interface CurrentBundleCardStats {
  read: number
  total: number
}

export interface CurrentBundleCardProps {
  bundle: CurrentBundleCardBundle
  stats: CurrentBundleCardStats
  onContinue?: () => void
}

// ---------------------------------------------------------------------------
// CurrentBundleCard
// ---------------------------------------------------------------------------

/**
 * Large card for the "In studio adesso" section.
 * Layout: large BundleCover (72×96) + centre column + "▶ Continua" CTA button.
 * Accent border + accent-bg shadow ring signals the active state.
 */
export function CurrentBundleCard({ bundle, stats, onContinue }: CurrentBundleCardProps) {
  const readPct = stats.total > 0 ? (stats.read / stats.total) * 100 : 0

  return (
    <div
      onClick={onContinue}
      className={clsx(
        'flex items-center bg-surface',
        /* design handoff — padding 20px 22px: py-5 (20px) + px-[22px] (no DS token for 22px) */
        'py-5 px-[22px]',
        /* design handoff — gap 24px: gap-6 (24px) ✓ */
        'gap-6',
        /* radius-lg = 14px ✓ */
        'rounded-lg',
        'border border-ar-accent',
        /* design handoff — accent-bg ring via box-shadow; no DS utility covers 0 0 0 3px accent-bg */
        onContinue != null ? 'cursor-pointer' : 'cursor-default',
      )}
      style={{
        /* brand-specific box-shadow ring: 0 0 0 3px accent-bg — not a DS shadow token */
        boxShadow: '0 0 0 3px var(--accent-bg)',
      }}
    >
      <BundleCover code={bundle.code} title={bundle.title} large />

      <div className="min-w-0 flex-1">
        {/* Eyebrow: cat · code · version */}
        {/* eyebrow fontSize 10px: text-xs is 11px, deviation 1px → round to text-xs */}
        <div className="mb-1 font-sans text-xs font-bold uppercase tracking-[0.14em] text-ar-accent">
          {bundle.cat} · {bundle.code} · v{bundle.version}
        </div>

        {/* Title — serif */}
        {/* design handoff — fontSize 24px: between text-xl(20) and text-2xl(26); deviation >1px; use arbitrary */}
        <div className="mb-[3px] font-serif text-[24px] font-semibold tracking-[-0.02em] text-ink">
          {bundle.title}
        </div>

        {/* Subtitle — italic EB Garamond */}
        {bundle.subtitle != null && (
          /* subtitle fontSize 13.5px: text-sm is 13px, deviation 0.5px → round to text-sm */
          <div className="mb-3 font-serif text-sm italic text-ink-3">{bundle.subtitle}</div>
        )}

        {/* Meta row: prof · exam date · days to exam */}
        {/* meta fontSize 11.5px: text-xs is 11px, deviation 0.5px → round to text-xs */}
        {/* design handoff — gap 14px: between space-3(12) and space-4(16); use arbitrary */}
        <div className="flex items-center gap-[14px] whitespace-nowrap font-sans text-xs text-ink-3">
          {bundle.prof != null && <span>{bundle.prof}</span>}
          {bundle.prof != null && bundle.examDate != null && (
            /* separator dot — 2.5×2.5px, no DS token */
            <span className="shrink-0 rounded-full bg-ink-4 w-[2.5px] h-[2.5px]" />
          )}
          {bundle.examDate != null && <span>esame {bundle.examDate}</span>}
          {bundle.daysToExam !== null && (
            <>
              {/* separator dot */}
              <span className="shrink-0 rounded-full bg-ink-4 w-[2.5px] h-[2.5px]" />
              <span
                className={clsx(
                  'font-mono font-medium',
                  bundle.daysToExam <= 14 ? 'text-red' : 'text-ink-3',
                )}
              >
                {bundle.daysToExam} giorni
              </span>
            </>
          )}
        </div>

        {/* Progress row */}
        <div className="mt-3">
          <div className="mb-[6px] flex items-baseline justify-between">
            <span className="font-mono text-xs font-semibold text-ink-2">
              {stats.read} <span className="text-ink-4">/ {stats.total}</span> letti
            </span>
            <span className="font-mono text-xs text-ink-3">{Math.round(readPct)}%</span>
          </div>
          {/* design handoff — height 4px: h-1 = 4px exact match */}
          <div className="h-1 overflow-hidden rounded-[2px] bg-surface-3">
            <div className="h-full bg-ar-accent" style={{ width: `${readPct}%` }} />
          </div>
        </div>
      </div>

      {/* CTA button */}
      {/* design handoff — padding 10px 18px, border-radius 9px, gap 7px: arbitrary values */}
      <div className="inline-flex shrink-0 cursor-pointer items-center self-center whitespace-nowrap bg-ink font-sans text-sm font-semibold text-bg rounded-[9px] py-[10px] px-[18px] gap-[7px]">
        ▶ Continua
      </div>
    </div>
  )
}
